import PropTypes from 'prop-types';
import React, { useState, useContext, Fragment, useEffect } from 'react';
import { AMENITIES } from '../../../config/text/text';
import { useScreenBreakpoint, isMobileViewport } from '../../../helpers/screen';
import ThemeContext from '../../../providers/ThemeProvider';
import MoreButton from '../../_generic/more-button/MoreButton';
import ScrollToOffset from '../../_generic/scroll-to-offset/ScrollToOffset';
import CollapsableSection from '../shared-components/CollapsableSection';
import { getIconMap } from './AmenitiesIconsMapping';

const AmenitiesDetails = ({
  amenities = [],
  className = '',
  collapsed = false,
  onToggle = () => {},
  isTravel = false,
}) => {
  const breakpoint = useScreenBreakpoint();

  const [showAll, setShowAll] = useState(false);
  const [maxNumber, setMaxNumber] = useState(3);
  const theme = useContext(ThemeContext);

  useEffect(() => {
    setMaxNumber(isMobileViewport() ? 2 : 3);
  }, [breakpoint]);

  if (!amenities) return null;

  const amenitiesMostPopular = amenities.find(
    (item) => item.type === 'Most Popular',
  );

  // If we have some amenities that don't fit in 'Most Popular' block,
  // we show the entire 'Most Popular' block in amenities detail section
  const isShowMostPopular = amenitiesMostPopular?.amenitiesData.length > 5;

  const amenitiesFiltered = isShowMostPopular
    ? amenities
    : amenities.filter((item) => item.type !== 'Most Popular');

  const moreToShow = amenitiesFiltered.length > maxNumber;

  // hide amenities
  if (!isTravel || amenitiesFiltered.length < 1) return null;

  const amenitiesDetail = (item, showItems, index) => {
    if (!showItems && index > maxNumber - 1) return null;

    return (
      <Fragment key={item.type}>
        <div className="col-6 col-lg-4 amenities__block">
          <div className="amenities__title">
            {getIconMap(item.type)}
            <b>{item.type}</b>
          </div>
          <ul className="amenities__list">
            {item.amenitiesData?.map((element) => {
              return (
                <li className="amenities__el" key={element}>
                  {element}
                </li>
              );
            })}
          </ul>
        </div>

        <style jsx>{`
          .amenities__block {
            color: ${theme.colors.primary};
            padding-bottom: 23px;
          }
          .amenities__title {
            align-items: center;
            display: flex;
            padding-bottom: 9px;
          }
          .amenities__list {
            list-style-type: disc;
            padding-left: 25px;
            margin: 0;
          }
          .amenities__el {
            position: relative;
          }
          :global(.amenities__title .svg-inline--fa) {
            flex-shrink: 0;
            margin-right: 10px;
          }
        `}</style>
      </Fragment>
    );
  };

  return (
    <>
      <div className="amenities-details">
        <ScrollToOffset id="amenities" />
        <CollapsableSection
          className={className}
          collapsed={collapsed}
          onToggle={onToggle}
          title={AMENITIES}
        >
          <div className="row">
            {amenitiesFiltered.map((item, index) => {
              return amenitiesDetail(item, showAll, index);
            })}
            {moreToShow && (
              <div className="col col-12 amenities-details__button">
                <MoreButton
                  expanded={showAll}
                  onToggle={() => setShowAll((state) => !state)}
                />
              </div>
            )}
          </div>
        </CollapsableSection>
      </div>

      <style jsx>{`
        .amenities__block {
          color: ${theme.colors.primary};
          padding-bottom: 23px;
        }
        .amenities__title {
          align-items: center;
          display: flex;
          padding-bottom: 9px;
        }
        .amenities__list {
          list-style-type: disc;
          padding-left: 25px;
          margin: 0;
        }
        .amenities__el {
          position: relative;
        }
        .amenities-details__button {
          margin-bottom: 10px;
        }
        :global(.amenities__title .svg-inline--fa) {
          flex-shrink: 0;
          margin-right: 10px;
        }
      `}</style>
    </>
  );
};

AmenitiesDetails.propTypes = {
  amenities: PropTypes.array,
  className: PropTypes.string,
  collapsed: PropTypes.bool,
  isTravel: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default AmenitiesDetails;
